<script>
import Card from '@/rex-components/rex-card.vue';
import Loading from '@/rex-components/rex-loading.vue';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import shortenLargeNumber from '@/utils/numberShortner';
import shortenTime from '@/utils/time/shortenTime.js';
import { getNumWithFixedDecimals } from '@/utils/helper-function';

import {
  capabilityLabelMap,
  capabilityIconNameMap,
  smryMetricKeyLabelMapper,
  APM_SERVICE_APDEX,
  BM_SERVICE_APDEX,
  VM_AVAILABILITY_STATUS,
  APDEX_FAIR_LIMIT,
  APDEX_POOR_LIMIT,
  PERCENTAGE_FORMAT,
  COUNT_FORMAT,
  TIME_FORMAT,
  REDIRECT_TO_ALERTS,
} from '@/components/Home/utils/custom.home.types';
import {
  CAPABILITY_APM,
  CAPABILITY_BROWSER,
  CAPABILITY_MOBILE,
  CAPABILITY_VM,
} from '@/utils/constants';
import { usePermissions } from '@/hooks/usePermissions';
import { HOME_RESOURCE_GROUP } from '@/router/constant';

export default {
  components: { Card, Loading },
  props: {
    smryConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters('globalState/settings', ['getDurationTime']),
    ...mapGetters('alertV2State/configure', ['getAlertCapabilities']),
    getIconName() {
      return capabilityIconNameMap[this.smryConfig.capability];
    },
    getCapabilityLabel() {
      return capabilityLabelMap[this.smryConfig.capability];
    },
    displayStatus() {
      return (
        this.smryConfig.capability !== CAPABILITY_MOBILE &&
        this.metricsList?.length > 0
      );
    },
    getIconColor() {
      if (this.smryConfig.capability === CAPABILITY_VM) {
        return this.healthStatus === 0
          ? 'rgb(241, 103, 85)'
          : 'rgb(59, 191, 85)';
      }
      const apdexVal = this.healthStatus / 10000;
      return apdexVal > APDEX_FAIR_LIMIT
        ? 'rgb(59, 191, 85)'
        : apdexVal <= APDEX_POOR_LIMIT
          ? 'rgb(241, 103, 85)'
          : 'rgb(239, 183, 0)';
    },
    hasWritePermission() {
      const { hasCapabilityWritePermission } = usePermissions();

      return hasCapabilityWritePermission(HOME_RESOURCE_GROUP);
    },
  },
  data() {
    return {
      metricsList: [],
      healthStatus: 0,
      vmMemoryTotal: 1,
      loading: false,
    };
  },
  methods: {
    ...mapActions('homeState', ['getSummaryMetricData']),
    ...mapActions('apmState/apmCommonState', ['selectService']),
    ...mapActions('browserState/browserCommonState', ['selectBrowserService']),
    ...mapActions('mobileState/mobileCommonState', ['selectMobileService']),
    ...mapActions('vmState/vmCommonState', ['selectVMService']),
    ...mapMutations('homeState', ['setdeleteWidgetId']),
    ...mapMutations('alertV2State/configure', [
      'setCurrentAlertApplication',
      'setCurrentAlertCapability',
    ]),
    formatMetricData(metricList) {
      const formattedList = [];
      let status = 0,
        vmMemoryTotal;
      metricList.forEach((obj) => {
        if (
          obj.metricName === APM_SERVICE_APDEX ||
          obj.metricName === BM_SERVICE_APDEX ||
          obj.metricName === VM_AVAILABILITY_STATUS
        )
          status = obj.value;
        else
          formattedList.push({
            metricLabel: smryMetricKeyLabelMapper[obj.metricName]?.label,
            value: obj.value,
            formatType: smryMetricKeyLabelMapper[obj.metricName]?.dataFormat,
            key: obj.metricName,
            aggregationNum:
              smryMetricKeyLabelMapper[obj.metricName]?.aggregationNum,
            redirectTo: smryMetricKeyLabelMapper[obj.metricName]?.redirectTo,
          });
      });
      return { formattedList, status, vmMemoryTotal };
    },
    async fetchMetricData() {
      try {
        this.loading = true;
        const params = {
          condition: {
            metricNames: this.smryConfig.metricName,
            entity: {
              normal: true,
              scope: this.smryConfig.entityType,
              serviceName: this.smryConfig.serviceKey,
            },
          },
          duration: this.getDurationTime,
          valueColumn: 'Summation',
        };
        // TODO: empty data & loaders needs to be added
        let { data } = await this.getSummaryMetricData(params);
        const { formattedList, status, vmMemoryTotal } = this.formatMetricData(
          data.dashboardSummary.metricsValue,
        );
        this.metricsList = formattedList;
        this.healthStatus = status;
        this.vmMemoryTotal = vmMemoryTotal;
        this.loading = false;
      } catch (error) {
        console.log('error', error);
        this.loading = false;
        this.metricsList = [];
      }
    },
    redirectToAlerts(serviceInfo) {
      this.$router.push({
        name: 'generatedV2',
        query: {
          id: serviceInfo.key,
        },
      });
    },
    onApplRedirect({ redirectTo }) {
      let serviceInfo = {
        key: this.smryConfig.serviceKey,
        label: this.smryConfig.serviceName,
      };
      if (redirectTo === REDIRECT_TO_ALERTS) {
        this.redirectToAlerts(serviceInfo);
        return;
      }
      const query = {
        id: serviceInfo.key,
      };
      switch (this.smryConfig.capability) {
        case CAPABILITY_APM:
          this.$router.push({
            name: 'dashboard',
            query,
          });
          break;
        case CAPABILITY_BROWSER:
          this.$router.push({
            name: 'browser',
            query,
          });
          break;
        case CAPABILITY_MOBILE:
          this.$router.push({
            name: 'mobile',
            query,
          });
          break;
        case CAPABILITY_VM:
          this.$router.push({
            name: 'vm',
            query,
          });
          break;
      }
    },
    formatMetricValue({ value, formatType, aggregationNum = 0 }) {
      switch (formatType) {
        case PERCENTAGE_FORMAT: {
          let calValue = value;
          if (aggregationNum > 0) calValue = calValue / aggregationNum;
          return `${getNumWithFixedDecimals(calValue)}%`;
        }
        case COUNT_FORMAT:
          return shortenLargeNumber(getNumWithFixedDecimals(value), 2);
        case TIME_FORMAT:
          return shortenTime(getNumWithFixedDecimals(value));
        default:
          return value;
      }
    },
  },
  mounted() {
    this.fetchMetricData();
  },
  watch: {
    getDurationTime() {
      this.fetchMetricData();
    },
  },
};
</script>

<template>
  <div>
    <Card>
      <template #card-header>
        <div class="cus-smry-header-box">
          <div class="cus-smry-header-widget-info">
            <div class="cus-home-drag-n-drop-pointer" v-if="hasWritePermission">
              <BaseIcon name="dragNDrop" width="0.813rem" height="1.25rem" />
            </div>
            <div class="cus-smry-status-icon" v-if="displayStatus">
              <BaseIcon
                name="status"
                width="1.25rem"
                height="1.25rem"
                :iconColor="getIconColor"
              />
            </div>
            <div>{{ smryConfig.widgetName }}</div>
            <div
              @click="() => setdeleteWidgetId(smryConfig.id)"
              class="cus-smry-delete-icon"
              v-if="hasWritePermission"
            >
              <BaseIcon
                name="deleteFilled"
                width=".9125rem"
                height=".9125rem"
              />
            </div>
          </div>

          <div class="cus-smry-header-cap-info">
            <div>
              <BaseIcon
                :name="getIconName"
                width=".9125rem"
                height=".9125rem"
                class="cus-smry-cap-icon"
              />
              <span class="cus-smry-cap-label">
                {{ getCapabilityLabel }}
              </span>
            </div>
            <div class="cus-smry-cap-name" @click="onApplRedirect">
              {{ smryConfig.serviceName }}
            </div>
          </div>
        </div>
      </template>

      <template #card-body>
        <div class="cus-smry-body-container">
          <Loading v-if="loading" />

          <div
            v-if="metricsList.length === 0 && !loading"
            class="cus-smry-body-no-data-box"
          >
            <div>
              <div><BaseIcon name="noData" /></div>
              <div class="cus-smry-body-err-msg">
                {{ $t('errWhileFetchingData') }}
              </div>
            </div>
          </div>

          <div
            class="cus-smry-body-box"
            v-if="metricsList.length > 0 && !loading"
          >
            <div
              v-for="(item, i) in metricsList"
              :key="i"
              class="cus-smry-metric-item"
              @click="() => onApplRedirect(item)"
            >
              <div class="cus-smry-metric-value">
                {{ formatMetricValue(item) }}
              </div>
              <div class="cus-smry-metric-label">
                {{ item.metricLabel }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
.cus-smry-header-box {
  @include flex-center-justify(space-between);
  width: 100%;
}
.cus-smry-header-widget-info {
  @include flex-center-vert;
}
.cus-smry-delete-icon {
  fill: $color-brand-accent;
  cursor: pointer;
  margin-left: 0.4rem;
  margin-top: -0.1rem;
}
.cus-smry-header-cap-info {
  @include flex-center-vert;
}
.cus-smry-cap-name {
  color: $color-brand-accent;
  cursor: pointer;
  font-family: $semi-bold;
  font-size: 0.75rem;
  &:hover {
    text-decoration: underline;
  }
  margin-left: 1rem;
}
.cus-smry-body-box {
  height: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(12rem, 1fr));
  gap: 2px;
}
.cus-smry-metric-item {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding-left: 2rem;
  max-height: 9rem;
  background-color: $bg-primary;
  cursor: pointer;
}
.cus-smry-metric-value {
  font-size: 2rem;
  font-weight: 400;
}
.cus-smry-metric-label {
  color: $font-tertiary;
}
.cus-smry-cap-icon {
  fill: $font-primary;
  margin-right: 0.3125rem;
}
.cus-smry-cap-label {
  color: $font-primary;
  font-family: $semi-bold;
  font-size: 0.75rem;
}
.cus-home-drag-n-drop-pointer {
  fill: $color-brand-accent;
  margin-right: 0.5rem;
  cursor: pointer;
}
.cus-smry-status-icon {
  margin-right: 0.5rem;
}
.cus-smry-body-container {
  height: 18.625em;
}
.cus-smry-body-no-data-box {
  @include flex-center-justify;
  height: 100%;
}
.cus-smry-body-err-msg {
  font-size: 1.25rem;
  color: $color-brand-accent;
}
</style>
